/**
 * Role selection scene
 * Professional card-based selection following design brief
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BaseScene, type SceneContext } from './BaseScene';
import { Button } from '@ui/Button';
import { theme } from '@core/theme';
import { app } from '@core/Application';
import { stateMachine } from '@core/StateMachine';
import type { UserRole } from '@core/types';

interface RoleOption {
  role: UserRole;
  title: string;
  description: string;
  icon: string;
  available: boolean;
}

const ROLES: RoleOption[] = [
  {
    role: 'pedestrian',
    title: 'Pedestrian',
    description: 'Navigate crosswalks, intersections, and busy streets safely',
    icon: '🚶',
    available: true,
  },
  {
    role: 'cyclist',
    title: 'Cyclist',
    description: 'Share the road with vehicles while staying visible and safe',
    icon: '🚴',
    available: true,
  },
  {
    role: 'driver',
    title: 'Driver',
    description: 'Make responsible decisions behind the wheel in traffic',
    icon: '🚗',
    available: true,
  },
];

export class RoleSelectScene extends BaseScene {
  private content!: Container;
  private roleCards: Container[] = [];
  private selectedRole: UserRole | null = null;
  private continueButton!: Button;

  constructor(context: SceneContext) {
    super(context);
  }

  async init(): Promise<void> {
    // Background
    const bg = new Graphics();
    bg.rect(0, 0, this.context.width, this.context.height);
    bg.fill(theme.colors.primaryBg);
    this.addChild(bg);

    // Content container
    this.content = new Container();
    this.addChild(this.content);

    // Title
    const title = new Text({
      text: 'Choose Your Role',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes['3xl'],
        fontWeight: theme.typography.weights.semibold,
        fill: theme.colors.textPrimary,
      }),
    });
    title.anchor.set(0.5, 0);
    this.content.addChild(title);

    // Subtitle
    const subtitle = new Text({
      text: 'Select how you want to experience the simulation',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.base,
        fill: theme.colors.textMuted,
      }),
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(0, 48);
    this.content.addChild(subtitle);

    // Role cards container
    const cardsContainer = new Container();
    cardsContainer.position.set(0, 120);
    this.content.addChild(cardsContainer);

    // Create role cards
    const cardWidth = 240;
    const cardGap = 24;
    const totalWidth = ROLES.length * cardWidth + (ROLES.length - 1) * cardGap;
    const startX = -totalWidth / 2 + cardWidth / 2;

    ROLES.forEach((role, index) => {
      const card = this.createRoleCard(role, index);
      card.position.set(startX + index * (cardWidth + cardGap), 0);
      cardsContainer.addChild(card);
      this.roleCards.push(card);
    });

    // Continue button
    this.continueButton = new Button({
      label: 'Continue',
      width: 160,
      height: 48,
      variant: 'primary',
    });
    this.continueButton.position.set(-80, 420);
    this.continueButton.setEnabled(false);
    this.continueButton.setOnClick(() => this.handleContinue());
    this.content.addChild(this.continueButton);

    // Back link
    const backText = new Text({
      text: '← Back',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fill: theme.colors.textMuted,
      }),
    });
    backText.anchor.set(0.5, 0);
    backText.position.set(0, 480);
    backText.eventMode = 'static';
    backText.cursor = 'pointer';
    backText.on('pointerover', () => (backText.style.fill = theme.colors.decision));
    backText.on('pointerout', () => (backText.style.fill = theme.colors.textMuted));
    backText.on('pointerdown', () => app.navigateTo('landing'));
    this.content.addChild(backText);

    this.setupKeyboard();
    this.positionContent();
  }

  private createRoleCard(role: RoleOption, index: number): Container {
    const card = new Container();
    const width = 240;
    const height = 260;

    const bg = new Graphics();
    card.addChild(bg);

    const drawCard = (hovered: boolean, selected: boolean) => {
      bg.clear();

      let bgColor: number = theme.colors.cardBg;
      let borderColor: number = theme.colors.neutralDark;
      let shadowAlpha = 0.1;

      if (!role.available) {
        bgColor = theme.colors.neutralDark;
        borderColor = theme.colors.neutralDark;
        shadowAlpha = 0.05;
      } else if (selected) {
        bgColor = theme.cardStates.selected.bg;
        borderColor = theme.colors.decision;
        shadowAlpha = 0.15;
      } else if (hovered) {
        bgColor = theme.cardStates.hover.bg;
        borderColor = theme.colors.neutral;
        shadowAlpha = 0.12;
      }

      // Shadow
      bg.roundRect(0, 4, width, height, theme.radii.lg);
      bg.fill({ color: 0x000000, alpha: shadowAlpha });

      // Main card
      bg.roundRect(0, 0, width, height, theme.radii.lg);
      bg.fill(bgColor);

      // Border
      bg.roundRect(0, 0, width, height, theme.radii.lg);
      bg.stroke({ width: selected ? 2 : 1, color: borderColor, alpha: 0.8 });
    };

    drawCard(false, false);

    // Icon
    const icon = new Text({
      text: role.icon,
      style: new TextStyle({ fontSize: 56 }),
    });
    icon.anchor.set(0.5, 0);
    icon.position.set(width / 2, 32);
    icon.alpha = role.available ? 1 : 0.4;
    card.addChild(icon);

    // Title
    const title = new Text({
      text: role.title,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.lg,
        fontWeight: theme.typography.weights.semibold,
        fill: role.available ? theme.colors.textPrimary : theme.colors.textMuted,
      }),
    });
    title.anchor.set(0.5, 0);
    title.position.set(width / 2, 110);
    card.addChild(title);

    // Description
    const desc = new Text({
      text: role.available ? role.description : 'Coming soon',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fill: role.available ? theme.colors.textSecondary : theme.colors.textMuted,
        wordWrap: true,
        wordWrapWidth: width - 40,
        align: 'center',
        lineHeight: theme.typography.sizes.sm * theme.typography.lineHeight.relaxed,
      }),
    });
    desc.anchor.set(0.5, 0);
    desc.position.set(width / 2, 140);
    card.addChild(desc);

    // Keyboard hint
    if (role.available) {
      const keyHint = new Text({
        text: `Press ${index + 1}`,
        style: new TextStyle({
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.sizes.xs,
          fill: theme.colors.textMuted,
        }),
      });
      keyHint.anchor.set(0.5, 0);
      keyHint.position.set(width / 2, 220);
      card.addChild(keyHint);
    }

    // Interaction
    if (role.available) {
      card.eventMode = 'static';
      card.cursor = 'pointer';

      card.on('pointerover', () => {
        if (this.selectedRole !== role.role) {
          drawCard(true, false);
        }
      });

      card.on('pointerout', () => {
        drawCard(false, this.selectedRole === role.role);
      });

      card.on('pointerdown', () => {
        this.selectRole(role.role, index);
      });
    }

    (card as Container & { redraw: () => void }).redraw = () => {
      drawCard(false, this.selectedRole === role.role);
    };

    return card;
  }

  private selectRole(role: UserRole, _index: number): void {
    this.selectedRole = role;
    this.roleCards.forEach((card) => {
      (card as Container & { redraw: () => void }).redraw?.();
    });
    this.continueButton.setEnabled(true);
  }

  private setupKeyboard(): void {
    const handleKeydown = (e: KeyboardEvent) => {
      if (!this.isActive) return;

      if (e.key === 'Enter' && this.selectedRole) {
        this.handleContinue();
      } else if (e.key >= '1' && e.key <= '3') {
        const index = parseInt(e.key) - 1;
        if (ROLES[index]?.available) {
          this.selectRole(ROLES[index].role, index);
        }
      }
    };

    window.addEventListener('keydown', handleKeydown);
    this.on('destroyed', () => {
      window.removeEventListener('keydown', handleKeydown);
    });
  }

  private handleContinue(): void {
    if (this.selectedRole) {
      stateMachine.setRole(this.selectedRole);
      app.navigateTo('scenario');
    }
  }

  private positionContent(): void {
    this.content.position.set(
      this.context.width / 2,
      this.context.height / 2 - 200
    );
  }

  protected handleResize(): void {
    const bg = this.children[0] as Graphics;
    bg.clear();
    bg.rect(0, 0, this.context.width, this.context.height);
    bg.fill(theme.colors.primaryBg);

    this.positionContent();
  }
}
